import { handleApiError } from '../core/apiError';
import axiosInstance from '../core/axiosInstance';

/**
 * Sends a request to the server to fetch the total stock count.
 * 
 * This function sends a `GET` request to the `/mobile-phone-details/count` endpoint 
 * to retrieve the total stock count of mobile phone details from the server. The 
 * server's response, which includes the stock count, will be returned.
 * It uses the default Axios instance with pre-configured headers and base URL.
 * 
 * @function
 * @returns {Promise<Object>} - A promise that resolves to the server's response data, which includes the total stock count.
 * @throws {Error} - Throws an error if the request fails.
 * 
 */

export const fetchTotalTenants = async () => {
  try {
    const response = await axiosInstance.get('/tenant/count');
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error; // Rethrow to handle it in the component
  }
};

/**
 * Sends a request to the server to fetch the total available brands count.
 * 
 * This function sends a `GET` request to the `/brands/count` endpoint 
 * to retrieve the total available brands count of mobile phone from the server. The 
 * server's response, which includes the brands count, will be returned.
 * It uses the default Axios instance with pre-configured headers and base URL.
 * 
 * @function
 * @returns {Promise<Object>} - A promise that resolves to the server's response data, which includes the total available brand count.
 * @throws {Error} - Throws an error if the request fails.
 * 
 */

export const fetchTotalRentalProperties = async () => {
  try {
    const response = await axiosInstance.get('/rental_property/count');
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error; // Rethrow to handle it in the component
  }
};

/**
 * Sends a request to the server to fetch the total available models count.
 * 
 * This function sends a `GET` request to the `/mobile-phones/models/count` endpoint 
 * to retrieve the total available models count of mobile phone from the server. The 
 * server's response, which includes the models count, will be returned.
 * It uses the default Axios instance with pre-configured headers and base URL.
 * 
 * @function
 * @returns {Promise<Object>} - A promise that resolves to the server's response data, which includes the total available model count.
 * @throws {Error} - Throws an error if the request fails.
 * 
 */

export const fetchBuildingCount = async () => {
  try {
    const response = await axiosInstance.get('/building/count');
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error; // Rethrow to handle it in the component
  }
};

/**
 * Sends a request to the server to fetch the 10 recent transactions.
 * 
 * This function sends a `GET` request to the `/daybook/recent-transactions` endpoint 
 * to retrieve recent transactions from the server. The server's response, which includes 
 * the list of top 10 recent transactions, will be returned.
 * It uses the default Axios instance with pre-configured headers and base URL.
 * 
 * @function
 * @returns {Promise<Object>} - A promise that resolves to the server's response data, which includes the recent transactions.
 * @throws {Error} - Throws an error if the request fails.
 * 
 */

export const fetchAnnualIncrement = async () => {
  try {
    const response = await axiosInstance.get('/tenant/annual-increment-date');
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};